import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IconUsers } from "@tabler/icons-react";
import { Heading } from "../../../../components/common/typography";
import { SearchBar } from "../../../../components/common/input";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import { SimpleTable } from "../../../../components/common/simple-table";
import { EmptyState } from "../../../../components/common/empty-state";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { Pagination } from "../../../../components/common/pagination";
import { AccentStatCard } from "../../../../components/common/accent-stat-card";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useAdminUsers } from "../../hooks/useAdminUsers";
import { useAdminUserStats } from "../../hooks/useAdminUserStats";
import { buildAdminUsersColumns } from "../components/admin-users-columns";
import { AdminUsersMobileList } from "../components/admin-users-mobile-list";
import type { AdminUserQueryDto } from "../../../../api";
import { AdminUserQueryDtoRoleEnum } from "../../../../api";

type RoleFilter = "all" | "admin" | "user";
type StatusFilter = "all" | "active" | "inactive";

const ROLE_OPTIONS: { value: RoleFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "admin", label: "Admin" },
  { value: "user", label: "User" },
];

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const VALID_ROLES = new Set<RoleFilter>(ROLE_OPTIONS.map((o) => o.value));
const VALID_STATUSES = new Set<StatusFilter>(STATUS_OPTIONS.map((o) => o.value));
const PAGE_SIZE = 10;

export function AdminUsersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const roleParam = searchParams.get("role") as RoleFilter | null;
  const role: RoleFilter = roleParam && VALID_ROLES.has(roleParam) ? roleParam : "all";
  const statusParam = searchParams.get("status") as StatusFilter | null;
  const status: StatusFilter = statusParam && VALID_STATUSES.has(statusParam) ? statusParam : "all";
  const search = searchParams.get("search") ?? "";
  const page = Number(searchParams.get("page") ?? "1") || 1;

  const setParam = (key: string, value: string | null, replace = false) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value);
    else p.delete(key);
    p.delete("page");
    setSearchParams(p, { replace });
  };

  const query: AdminUserQueryDto = {
    search: search.trim() || undefined,
    role: role === "all" ? undefined : (role as AdminUserQueryDtoRoleEnum),
    isActive: status === "all" ? undefined : status === "active",
    page,
    pageSize: PAGE_SIZE,
  };

  const stats = useAdminUserStats();
  const { data, isLoading } = useAdminUsers(query);
  const users = data?.data ?? [];
  const pageCount = data?.meta?.pageCount ?? 1;

  const columns = useMemo(() => buildAdminUsersColumns(), []);

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:gap-4 max-md:px-4 max-md:py-6">
      <Heading variant="h2" tone="dark">User Management</Heading>

      <div className="grid grid-cols-3 gap-4 max-md:grid-cols-1 max-md:gap-3">
        <AccentStatCard accent="green" label="Total Users" value={stats.total.toLocaleString()} />
        <AccentStatCard accent="cyan" label="Active Users" value={stats.active.toLocaleString()} />
        <AccentStatCard accent="amber" label="Admin Users" value={stats.admins.toLocaleString()} />
      </div>

      <div className="flex items-center gap-3 max-md:flex-col max-md:items-stretch">
        <div className="flex-1">
          <SearchBar
            value={search}
            // No .trim() here: it runs on every keystroke, which makes spaces
            // untypable and so blocks multi-word search. `query` already trims
            // before the value reaches the API. `replace` keeps per-keystroke
            // typing out of the browser history.
            onChange={(e) => setParam("search", e.target.value || null, true)}
            placeholder="Search by name or email"
            wrapperClassName=""
          />
        </div>
        <SegmentedControl
          options={ROLE_OPTIONS}
          value={role}
          onChange={(v) => setParam("role", v === "all" ? null : v)}
          aria-label="Filter by role"
          className="max-md:w-full max-md:justify-between"
        />
        <SegmentedControl
          options={STATUS_OPTIONS}
          value={status}
          onChange={(v) => setParam("status", v === "all" ? null : v)}
          aria-label="Filter by status"
          className="max-md:w-full max-md:justify-between"
        />
      </div>

      {isLoading ? (
        <ShimmerLoader message="Loading users…" lines={6} />
      ) : users.length === 0 ? (
        <div className="rounded-2xl border border-borderDefault bg-bgWhite">
          <EmptyState icon={IconUsers} title="No users found" description="Try adjusting your search or filters." />
        </div>
      ) : (
        <>
        <AdminUsersMobileList
          users={users}
          onSelect={(u) => navigate(`${AppRoutes.ADMIN_USERS}/${u.id}`)}
          footer={
            pageCount > 1 ? <PaginationFooter page={page} pageCount={pageCount} /> : undefined
          }
        />
        <SimpleTable
          className="max-md:hidden"
          columns={columns}
          data={users}
          keyExtractor={(u) => u.id}
          onRowClick={(u) => navigate(`${AppRoutes.ADMIN_USERS}/${u.id}`)}
          footer={
            pageCount > 1 ? (
              <PaginationFooter page={page} pageCount={pageCount} />
            ) : undefined
          }
        />
        </>
      )}
    </div>
  );
}

function PaginationFooter({ page, pageCount }: { page: number; pageCount: number }) {
  const [searchParams, setSearchParams] = useSearchParams();
  return (
    <div className="flex justify-center">
      <Pagination
        page={page}
        pageCount={pageCount}
        onPageChange={(next) => {
          const p = new URLSearchParams(searchParams);
          p.set("page", String(next));
          setSearchParams(p, { replace: true });
        }}
      />
    </div>
  );
}
